import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import SessionCard from '../../components/training/SessionCard';
import trainingApi from '../../services/trainingApi';

const SessionHistory = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  const fetchSessions = useCallback(async (page = 1) => {
    try {
      setIsLoading(true);
      const params = { page, limit: 20, sort: '-startedAt' };
      if (statusFilter) params.status = statusFilter;

      const response = await trainingApi.listSessions(params);
      const body = response.data || response;
      const data = body.data || body;

      const sessionsList = Array.isArray(data.sessions) ? data.sessions :
                          Array.isArray(data) ? data : [];
      setSessions(sessionsList);
      setPagination(data.pagination || { page, limit: 20, total: sessionsList.length, pages: 1 });
    } catch (err) {
      console.error('[SessionHistory] Error loading sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchSessions(1);
  }, [fetchSessions]);

  const handlePageChange = (_, page) => {
    fetchSessions(page);
  };

  const handleViewFeedback = (sessionId) => {
    navigate(`/training/session/${sessionId}/feedback`);
  };

  const handleContinueSession = (sessionId) => {
    navigate(`/training/session/${sessionId}`);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>My Sessions</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View all your training sessions and access feedback.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filter */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="abandoned">Cancelled</MenuItem>
            <MenuItem value="timed_out">Timed Out</MenuItem>
            <MenuItem value="awaiting_formulation">Awaiting Formulation</MenuItem>
            <MenuItem value="awaiting_evaluation">Evaluating</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && sessions.length === 0 && (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {statusFilter ? 'No sessions matching this filter.' : 'No sessions yet. Start your first training scenario from the Scenario Library.'}
        </Typography>
      )}

      {/* Session list */}
      {!isLoading && sessions.length > 0 && (
        <>
          {sessions.map(session => (
            <SessionCard
              key={session._id}
              session={session}
              onViewFeedback={handleViewFeedback}
              onContinue={handleContinueSession}
            />
          ))}

          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SessionHistory;
